import { apiClient } from './api.client';

export interface S3Configuration {
  id?: number;
  company_id?: number;
  bucket_name: string;
  region: string;
  access_key_id: string;
  secret_access_key: string;
  folder_structure: 'location' | 'date' | 'location_date';
  is_active: boolean;
  ai_metadata_enabled: boolean;
  event_video_upload_enabled: boolean;
  created_at?: string;
  updated_at?: string;
  is_inherited?: boolean;
}

export interface S3TestResult {
  success: boolean;
  message: string;
  accessible?: boolean;
}

class IntegrationService {
  private readonly endpoint = '/integrations/s3';

  /**
   * Get S3 configuration for current company
   */
  async getS3Config(): Promise<S3Configuration | null> {
    try {
      const response = await apiClient.get<S3Configuration>(this.endpoint);
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No configuration exists yet
      }
      throw error;
    }
  }

  /**
   * Create new S3 configuration
   */
  async createS3Config(config: Omit<S3Configuration, 'id' | 'company_id' | 'created_at' | 'updated_at'>): Promise<S3Configuration> {
    const response = await apiClient.post<S3Configuration>(this.endpoint, config);
    return response;
  }

  /**
   * Update existing S3 configuration
   */
  async updateS3Config(config: S3Configuration): Promise<S3Configuration> {
    const response = await apiClient.put<S3Configuration>(this.endpoint, config);
    return response;
  }

  /**
   * Delete S3 configuration
   */
  async deleteS3Config(): Promise<void> {
    await apiClient.delete(this.endpoint);
  }

  /**
   * Test S3 connection with provided credentials
   */
  async testS3Connection(config: Omit<S3Configuration, 'id' | 'company_id' | 'created_at' | 'updated_at'>): Promise<S3TestResult> {
    const response = await apiClient.post<S3TestResult>(`${this.endpoint}/test`, config);
    return response;
  }

  /**
   * Upload event media to S3
   * This is called automatically by the backend when events are created
   * But can also be manually triggered for existing events
   */
  async uploadEventToS3(eventId: number): Promise<{ success: boolean; s3_url?: string; message: string }> {
    const response = await apiClient.post<{ success: boolean; s3_url?: string; message: string }>(
      `/integrations/s3/upload-event/${eventId}`
    );
    return response;
  }

  /**
   * Bulk upload existing events to S3
   */
  async bulkUploadEventsToS3(locationId?: number): Promise<{ success: boolean; uploaded_count: number; failed_count: number; message: string }> {
    const endpoint = locationId 
      ? `/integrations/s3/bulk-upload?location_id=${locationId}`
      : '/integrations/s3/bulk-upload';
    const response = await apiClient.post<{ success: boolean; uploaded_count: number; failed_count: number; message: string }>(
      endpoint,
      {}
    );
    return response;
  }

  /**
   * Migrate existing events from external clouds to user's S3
   * 
   * Downloads images from HikVision, EZVIZ, and other cloud providers
   * and re-uploads to user's own S3 bucket for full control
   */
  async migrateEventsToS3(locationId?: number, limit: number = 100): Promise<{
    success: boolean;
    migrated: number;
    failed: number;
    skipped: number;
    total_processed: number;
    message: string;
  }> {
    const params = new URLSearchParams();
    if (locationId) params.append('location_id', locationId.toString());
    params.append('limit', limit.toString());
    
    const endpoint = `/integrations/s3/migrate-events${params.toString() ? '?' + params.toString() : ''}`;
    const response = await apiClient.post<{
      success: boolean;
      migrated: number;
      failed: number;
      skipped: number;
      total_processed: number;
      message: string;
    }>(endpoint, {});
    return response;
  }

  /**
   * Get locations that have external access (DDNS) configured
   * Required for automatic event video recording
   */
  async getLocationsWithExternalAccess(): Promise<{
    success: boolean;
    locations: Array<{
      id: number;
      name: string;
      external_hostname: string;
      external_http_port: number;
    }>;
    count: number;
  }> {
    const response = await apiClient.get<{
      success: boolean;
      locations: Array<{
        id: number;
        name: string;
        external_hostname: string;
        external_http_port: number;
      }>;
      count: number;
    }>('/integrations/s3/locations-with-external-access');
    return response;
  }

  /**
   * Get S3 video URL for an event if it exists
   */
  async getEventVideoUrl(eventId: number): Promise<{
    success: boolean;
    event_id: number;
    has_video: boolean;
    video_url: string | null;
    duration: number | null;
  }> {
    const response = await apiClient.get<{
      success: boolean;
      event_id: number;
      has_video: boolean;
      video_url: string | null;
      duration: number | null;
    }>(`/integrations/event/${eventId}/video-url`);
    return response;
  }
}

export const integrationService = new IntegrationService();